import { NgClass, NgIf } from "@angular/common";
import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    computed,
    signal,
    viewChild
} from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ICompany, Module, RequestLicense, RequestLicenseUserSet, RequestModule, RequestRole, Role } from '@myapp/features/admin/models';
import { AdminService } from "@myapp/features/admin/services";
import { AuthPermIdentifier, AuthPermIdentifierParent, TreeInfoAuth, UserSummary } from "@myapp/modules/fwcore/models";
import { RequestUserPermission, RequestUserPermissionSet, ResponseUserPermission } from "@myapp/modules/fwcore/models/messages";
import {
    AuthorizationService, ConfirmationDialogHtmlPaneService, ConfirmationDialogService,
    NotificationDialogService, NotificationPopupService
} from "@myapp/modules/fwcore/services";
import { IsgHelperService } from "@mysrc/services/framework";
import { ButtonModule } from "@progress/kendo-angular-buttons";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { IsgSizerDirective } from "../../../prob/directives/isg-sizer.directive";
import { AuthTreeComponent } from "../../components/auth/auth-tree/auth-tree.component";
import { ModulesListComponent } from "../../components/modules/modules-list/modules-list.component";
import { RolesListComponent } from "../../components/roles/roles-list/roles-list.component";
import { UserListComponent } from "../../components/users/user-list/user-list.component";

@Component({
  selector: 'app-page-user-permission',
  standalone: true,
  imports: [RouterLink, ButtonModule, IsgSizerDirective, UserListComponent, NgClass, ModulesListComponent, NgIf, RolesListComponent, AuthTreeComponent],
  templateUrl: './page-user-permission.component.html',
  styleUrls: ['./page-user-permission.component.scss']
})
export class PageUserPermissionComponent implements OnInit, OnDestroy, AfterViewInit {

  //----------------------------  Constants  ---------------------------

  /** Permission code allowing user-permission assignment. */
  protected readonly ctCodeUserPermSet = 'user-perm-set';

  /** Permission code allowing user-role assignment. */
  protected readonly ctCodeUserRoleSet = 'user-role-set';

  /** Dialog message shown when a save fails. */
  protected readonly ctMsgError = 'Something went wrong';

  /** Bottom offset used when calculating the page height. */
  protected readonly ctOffsetBottomHeight = 21;

  /** Top offset used when calculating the page height. */
  protected readonly ctOffsetTopHeight = 54;

  /** Process filter value to add roles to users. */
  protected readonly ctProcessAdd = 1;

  /** Process filter value to assign (replace) user roles. */
  protected readonly ctProcessAssign = 0;

  /** Process filter value to remove roles from users. */
  protected readonly ctProcessRemove = -1;

  /** Route to the license admin page. */
  protected readonly ctRouteLicense = './admin/license';

  /** Route to the role admin page. */
  protected readonly ctRouteRole = './admin/role';

  /** Route to the user license admin page. */
  protected readonly ctRouteUserLicense = './admin/user/license';

  /** Route to the user permission admin page. */
  protected readonly ctRouteUserPermission = './admin/user/permission';

  /** Bottom offset used when sizing the sibling controls. */
  protected readonly ctSiblingOffsetBottomHeight = 29;

  /** Top offset used when sizing the sibling controls. */
  protected readonly ctSiblingOffsetTopHeight = 6;

  /** Source value identifying user-sourced permissions. */
  protected readonly ctSourceUser = 'User';

  /** Dialog title for failed operations. */
  protected readonly ctTitleError = 'Error';

  /** Dialog title for successful operations. */
  protected readonly ctTitleSuccess = 'Success';

  //----------------------  ViewChild Properties  ----------------------

  /** Reference to the modules list component. */
  protected moduleListComponent = viewChild(ModulesListComponent);

  /** Reference to the roles list component. */
  protected rolesListComponent = viewChild(RolesListComponent);

  /** Reference to the visual auth tree component. */
  protected authTreeVisualComponent = viewChild<AuthTreeComponent>('authtreevisual');

  /** Reference to the data auth tree component. */
  protected authTreeDataComponent = viewChild<AuthTreeComponent>('authtreedata');

  /** Reference to the user list component. */
  protected userListComponent = viewChild(UserListComponent);

  //------------------------  Public Properties  -----------------------

  /** Id of the current license. */
  public currentLicenseId: string | null = null;

  /** Id of the current company. */
  public currentCompanyId: string | null = null;

  /** Id of the currently selected user. */
  public currentUserId: string | null = null;

  /** Currently selected user. */
  public currentUser: UserSummary | null = null;

  /** Id of the currently selected module. */
  public currentModuleId!: string;

  /** Id of the currently selected role. */
  public currentRoleId: string | undefined | null = null;

  /** Currently selected role. */
  public currentRole: Role | null = null;

  /** Name of the current company. */
  public companyName: string | undefined | null = null;

  /** Triggers a permissions refresh when changed. */
  public saveDislayPermissions: boolean | null = null;

  //----------------------  Navigation Properties  ---------------------

  /** Link to the license admin page. */
  protected hrefLicenses: string = this.ctRouteLicense;

  /** Link to the user license admin page. */
  protected hrefUserManagement: string = this.ctRouteUserLicense;

  /** Link to the role admin page. */
  protected hrefLicenseRoles: string = this.ctRouteRole;

  /** Link to the user permission admin page. */
  protected hrefUserPermission: string = this.ctRouteUserPermission;

  //------------------------  State Properties  ------------------------

  /** Emits on component destruction to complete open subscriptions. */
  protected destroy$ = new Subject();

  /** True when permission edits are enabled. */
  protected enablePermissionEdits: boolean = false;

  /** True when the save-permissions action is enabled. */
  protected enableSavePermissions: boolean = false;


  /** True when the auth data tree should be hidden. */
  protected hideAuthDataTree: boolean = false;

  /** True when the module list should be hidden. */
  protected hideModule: boolean = false;

  /** User filter id applied to both auth trees via template binding. */
  protected treeUserFilterId: string | null | undefined = null;

  //-------------------  Data Collection Properties  -------------------

  /** Companies available for display. */
  protected companies: ICompany[] | undefined;

  //----------------------  Permission Properties  ---------------------

  /** Permission request payload for the current route. */
  protected reqPerm: RequestUserPermission = {
  }

  /** Permissions returned for the current user/route. */
  protected perms?: AuthPermIdentifier[] = [];

  /** Permission codes resolved for the current user/route. */
  protected permCodes = signal<Set<string>>(new Set<string>());

  /** Placeholder page-access check - permissive by default.
   *  Devs apply component-specific logic here when required. */
  protected hasPageAccess = computed(() => true);

  /** True once the user-permissions request has completed. */
  protected permsLoaded = signal<boolean>(false);

  /** True when the user may save user-role assignments. */
  protected showSaveUserRolesButton = computed<boolean>(() => this.permCodes().has(this.ctCodeUserRoleSet));

  /** True when the user may save user-permission assignments. */
  protected showSaveUserAuthsButton = computed<boolean>(() => this.permCodes().has(this.ctCodeUserPermSet));

  //---------------------------  Constructor  --------------------------

  constructor(protected adminService: AdminService
    , protected authService: AuthorizationService
    , protected notification: NotificationPopupService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogNotification: NotificationDialogService
    , protected dialogConfirmPane: ConfirmationDialogHtmlPaneService
    , protected activatedRoute: ActivatedRoute
    , protected router: Router
    , protected hlp: IsgHelperService) {
  }

  //------------------------  Lifecycle Methods  -----------------------

  /**
   * Initializes route params, companies, permissions and sizing
   */
  ngOnInit() {

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (params) => {

          // check the input id value before assignment to avoid injection
          this.currentLicenseId = this.hlp.getValidGUID(params.get('id'));

          if (!this.currentLicenseId) {
            this.router.navigate([this.ctRouteLicense]);
          }

          this.hrefLicenses = this.ctRouteLicense + `?id=` + this.currentLicenseId;
          this.hrefUserManagement = this.ctRouteUserLicense + "?id=" + this.currentLicenseId;
          this.hrefLicenseRoles = this.ctRouteRole + `?id=` + this.currentLicenseId;
          this.hrefUserPermission = this.ctRouteUserPermission + `?id=` + this.currentLicenseId;
        }
      );

    this.adminService.getAllCompanies().pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => { this.companies = resp; }
    );

    this.loadUserPermissions(this.reqPerm);

  }

  /**
   * Initializes the module list after view init
   */
  ngAfterViewInit() {

    this.moduleListComponent()?.Initialized();
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy() {

    this.destroy$.next();
    this.destroy$.complete();
  }

 
  //-------------------------  Event Handlers  -------------------------

  /** Handles user selection; resets modules, roles and permissions. */
  protected onUserSelected(ev: UserSummary | null) {

    this.currentUser = ev;
    this.currentUserId = ev?.id ?? null;

    const moduleList = this.moduleListComponent();

    if (moduleList) {
      moduleList.setCheckedIds([]);
      moduleList.selectedItem = null;
    }

    this.getUserModules();

    // unselect roles
    const rolesList = this.rolesListComponent();

    if (rolesList) {
      rolesList.setCheckedIds([]);
      rolesList.clearSelection();
    }

    this.getUserRoles();

    this.getUserAdminPermissions();
  }

  /** Handles module selection and initializes the user list. */
  protected onModuleSelected(ev: Module | null) {

    if (ev?.id) {
      this.currentModuleId = ev.id;
      this.userListComponent()?.Initialized();
    }
  }

  /** Hides the module list when fewer than two modules are loaded. */
  protected onModulesLoaded(count: number | null) {

    let v = !!(count) ? (count < 2) : true;

    this.hideModule = v;
  }

  /** Handles role selection and updates the auth tree messaging. */
  protected onRoleSelected(role: Role | null) {

    this.currentRole = role;
    this.currentRoleId = role?.id;

    this.treeUserFilterId = null;

    const treeVisual = this.authTreeVisualComponent();
    const treeData = this.authTreeDataComponent();

    if (this.currentRole) {

      const msg = `Role Permissions for: ${this.currentRole.name}`;

      if (treeVisual) {
        treeVisual.messageInfo = msg;
      }

      if (treeData) {
        treeData.messageInfo = msg;
      }
    }
    else {

      if (treeVisual) {
        treeVisual.messageInfo = null;
      }

      if (treeData) {
        treeData.messageInfo = null;
      }
    }

    this.enableSavePermissions = false;
  }

  /** Hides the auth data tree when no items are loaded. */
  protected onDataTreeLoaded(count: number | null) {

    let v = !!(count) ? (count < 1) : true;

    this.hideAuthDataTree = v;
  }

  /** Refreshes the user admin permissions. */
  public onPermissionsChanged() {

    this.getUserAdminPermissions();
  }

  /** Validates selections, confirms and saves user role changes. */
  protected onSaveUserRolesClick(process: number) {

    const userList = this.userListComponent();
    const rolesList = this.rolesListComponent();

    if (!userList
      || !rolesList) {
      return;
    }

    const customRoles = rolesList.getCheckedDataItems().filter(x => x.allLicenseUsers != true);
    const userNames = userList.getCheckedNames().join('</li><li>');
    const roleNames = customRoles.map(y => y.name).join('</li><li>');

    if (userList.checkedIds.length < 1) {
      this.notification.showNotification("Please select users to process by checking their boxes.", "warning");
      return;
    }
    else if (process != this.ctProcessAssign
      && customRoles.length < 1) {
      this.notification.showNotification("Please select roles to process by checking their boxes (excl. All user role types).", "warning");
      return;
    }

    let msg = '';

    if (process == this.ctProcessAdd) {
      msg = '<p>Click Yes to <u>add</u> role(s): </p><ul><li>'
        + roleNames
        + '</li></ul><p>to</p><ul><li>'
        + userNames
        + '</li></ul>';
    }
    else if (process == this.ctProcessAssign) {

      if (customRoles.length > 0) {
        msg = '<p>Are you sure you want to <u>assign</u> the role(s) below:</p><ul><li>'
          + roleNames
          + '</li></ul><p>to the user(s) below? This will replace their roles and <u>remove</u> any that are not in the list above (Roles assigned to all users will still apply).</p><ul><li>'
          + userNames
          + '</li></ul>';
      }
      else {
        msg = '<p>Are you sure you want to <u>remove all</u> the custom roles for:</p><ul><li>'
          + userNames
          + '</li></ul>';
      }
    }
    else if (process == this.ctProcessRemove) {
      msg = '<p>Click Yes to <u>remove</u> role(s): </p><ul><li>'
        + roleNames
        + '</li></ul><p>from</p><ul><li>'
        + userNames
        + '</li></ul>';
    }

    this.dialogConfirmPane.open('Confirm Save', msg, 400)
      .subscribe((result: any) => {

        if (result.value) {

          const req: RequestUserPermissionSet = {
            moduleId: this.currentModuleId
            , licenseId: this.currentLicenseId
            , userIds: userList.checkedIds
            , updateRoles: true
            , updatePerms: false
            , roleIds: rolesList.getCheckedIds()
            , authTypes: null
            , hierarchyIds: null
            , processFilter: process
          }

          this.setUserPermissions(req);
        }
      });
  }

  /** Validates selections, confirms and updates user license assignment. */
  protected onUpdateUserLicenseClick(adding: boolean) {

    const userList = this.userListComponent();

    if (!userList) {
      return;
    }

    let items: UserSummary[] | null;

    if (adding)
      items = userList.getCheckedItems().filter(x => x.assigned == false);
    else
      items = userList.getCheckedItems().filter(x => x.assigned == true);

    if (items.length < 1
      || !(items)) {
      this.notification.showNotification("No users have been checked to update.", "warning");
      return;
    }

    let msg = '';

    if (adding) {
      msg = '<p>Click Yes to <u>add</u> the user(s) below to the license: </p><ul><li>'
        + items.map(n => n.displayName).join('</li><li>')
        + '</li></ul>';
    }
    else {
      msg = '<p>Click Yes to <u>remove</u> the user(s) below from the license: </p><ul><li>'
        + items.map(n => n.displayName).join('</li><li>')
        + '</li></ul>';
    }

    this.dialogConfirmPane.open('Confirm License Change', msg, 300)
      .subscribe((result: any) => {

        if (result.value) {

          const req: RequestLicenseUserSet = {
            licenseId: this.currentLicenseId
            , assign: adding
            , data: items!.map(m => m.id!)
          };

          this.updateUserLicense(req);
        }
      });
  }

  //-------------------  Data Transformation Methods  ------------------

  /** Dedupes permissions, resolves exclusions and applies them to the trees. */
  protected refreshUserPermissions(items: AuthPermIdentifierParent[] | null | undefined) {

    // need a collection of TreeInfo which should be unique
    let dic = new Map<string, AuthPermIdentifierParent>();
    let setExclParents = new Set<string | null>();
    let setCustomUserParents = new Set<string | null>();

    // the collection returned may have duplicate authorizations, where one may be an exclusion
    // So we create a hash collection based on id then if we get a duplicate id we check so that the exclusion will remain
    if (items) {

      let key: string = "";
      let val: any;

      for (let i = 0; i < items.length; i++) {

        val = items[i];
        key = val.id!;

        if (!dic.has(key)) {
          val.sourceType = "role";
          dic.set(key, val);
        }
        else {

          let tr: any = dic.get(key);

          if (!tr.excluded) {

            if (val.excluded) {
              dic.delete(key);
              val.sourceType = "excl";
              dic.set(key, val);
            }
            else {
              tr.sourceType += val.source ?? "";
            }
          }
        }

        if (val.excluded === true) {
          setExclParents.add(val.parentId);
        }
        else if (val.source === this.ctSourceUser) {
          setCustomUserParents.add(val.parentId);
        }
      }
    }

    for (let idParent of setExclParents) {
      this.recursiveChildHasExclusions(idParent, dic);
    }

    for (let idParent of setCustomUserParents) {
      this.recursiveChildHasInclusion(idParent, dic);
    }

    let parsedItems: TreeInfoAuth[] = Array.from(dic.values());

    const treeVisual = this.authTreeVisualComponent();
    const treeData = this.authTreeDataComponent();

    treeVisual?.setCheckedSelection(parsedItems);
    treeData?.setCheckedSelection(parsedItems);

    this.treeUserFilterId = this.currentUser?.id;

    if (this.currentUser) {

      const msg = `User permissions for ${this.currentUser.displayName}`;

      if (treeVisual) {
        treeVisual.messageInfo = msg;
      }

      if (treeData) {
        treeData.messageInfo = msg;
      }
    }
    else {

      if (treeVisual) {
        treeVisual.messageInfo = null;
      }

      if (treeData) {
        treeData.messageInfo = null;
      }
    }

    this.enableSavePermissions = !!(this.currentUser?.assigned);
  }

  /** Recursively flags parent items as having excluded children. */
  protected recursiveChildHasExclusions(id: string | undefined | null, lookup: Map<string, any>) {

    if (!id)
      return;

    let parent = lookup.get(id);

    if (parent) {
      parent.hasChildExcludedIds = true;
      this.recursiveChildHasExclusions(parent.parentId, lookup);
    }
  }

  /** Recursively flags parent items as having custom included children. */
  protected recursiveChildHasInclusion(id: string | undefined | null, lookup: Map<string, any>) {

    if (!id)
      return;

    let parent = lookup.get(id);

    if (parent) {
      parent.hasChildCustomIds = true;
      this.recursiveChildHasInclusion(parent.parentId, lookup);
    }
  }

  //---------------------------  Api Methods  --------------------------

  /** Fetches user permissions, gates page access and redirects if denied. */
  protected loadUserPermissions(reqPerm: RequestUserPermission): void {

    this.authService.getData$<RequestUserPermission, ResponseUserPermission>(reqPerm, this.authService.endpointUserPermission)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {

        this.perms = resp.data ?? [];

        this.permCodes.set(new Set(this.perms.map(p => p.code).filter((c): c is string => !!c)));

        this.permsLoaded.set(true);

        if (!this.hasPageAccess()) {
          this.router.navigate(['/']);
        }
      });
  }

  /** Fetches the license and applies the company details. */
  protected updateCompanyInfo() {

    let req: RequestLicense = { id: this.currentLicenseId! };

    this.adminService.getLicense$(req).pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => {

        if (resp.data
          && resp.data.length === 1) {
          this.companyName = resp.data[0].companyName;
          this.currentCompanyId = (resp.data[0].companyId) ? resp.data[0].companyId : null;
        }
        else {
          this.companyName = "";
          this.currentCompanyId = null;
        }
      }
    );
  }

  /** Fetches the current user's modules and applies the checked ids. */
  protected getUserModules() {

    const req: RequestModule = {
      licenseId: this.currentLicenseId
      , userFilterId: this.currentUser?.id
      , active: null
      , hasApi: null
      , includeHost: true
    };

    this.adminService.getModules$(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        resp => {

          if (req.userFilterId) {

            const ids = resp.data!.map((x: any) => x.id!);

            this.moduleListComponent()?.setCheckedIds(ids);
          }
          else {
            this.moduleListComponent()?.setCheckedIds([]);
          }
        }
      );
  }

  /** Fetches the current user's roles and applies the checked ids. */
  protected getUserRoles() {

    const req: RequestRole = {
      licenseId: this.currentLicenseId
      , moduleId: this.currentModuleId
      , userFilterId: this.currentUser?.id
    };

    this.adminService.getRole$(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        resp => {

          if (req.userFilterId) {
            this.rolesListComponent()?.setCheckedIds(resp.data!.map((x: any) => x.id!));
          }
          else {
            this.rolesListComponent()?.setCheckedIds([]);
          }
        }
      );
  }

  /** Fetches the current user's admin permissions and refreshes the trees. */
  protected getUserAdminPermissions() {

    if (this.currentUser == null
      || !this.currentUser.assigned) {
      this.refreshUserPermissions(null);
      return;
    }

    const userPerm: RequestUserPermission = {
      userFilterId: this.currentUser?.id
      , moduleId: this.currentModuleId
      , includeExclusions: true
    };

    this.authService.userPermissionAdminGet$(userPerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        resp => {
          this.refreshUserPermissions(resp.data);
        }
      );
  }

  /** Persists the user permission/role changes and notifies the result. */
  protected setUserPermissions(req: RequestUserPermissionSet) {

    this.authService.userPermissionSet$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => {
        }))
      .subscribe((res) => {

        if (res.success) {

          if (req.updateRoles)
            this.dialogNotification.openNotificationDialog(this.ctTitleSuccess, 'Roles saved successfully');
          else
            this.dialogNotification.openNotificationDialog(this.ctTitleSuccess, 'Permissions saved successfully');

          const userList = this.userListComponent();

          if (userList) {
            userList.checkedIds = [];
          }
        }
        else {
          this.dialogNotification.openNotificationDialog(this.ctTitleError, this.ctMsgError);
        }
      });
  }

  /** Persists the user license assignment changes and applies them locally. */
  protected updateUserLicense(req: RequestLicenseUserSet) {

    this.authService.licenseUserSet$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => {
        }))
      .subscribe((res) => {

        if (res.success) {

          this.dialogNotification.openNotificationDialog(this.ctTitleSuccess, 'Users updated');

          const userList = this.userListComponent();

          if (userList) {

            const checked = userList.getCheckedItems();

            checked?.forEach(x => x.assigned = res.assign);

            userList.clearCheckedItems();

            const licenseId = userList.licenseId();

            if (licenseId
              && licenseId.length > 0
              && res.assign == false) {
              userList.removeItems(checked);
            }
          }
        }
        else {
          this.dialogNotification.openNotificationDialog(this.ctTitleError, this.ctMsgError);
        }
      });
  }

}
